import React, { useReducer } from 'react';
import styled from 'styled-components';
import { isMobile } from 'react-device-detect';
import { ThemeProvider } from 'styled-components';
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
  StyledEngineProvider,
} from '@mui/material/styles';
import { ModalProvider, BaseModalBackground } from 'styled-react-modal';
// custom
import { GlobalStyles, theme } from './styles/GlobalStyles';
import reducer from './reducers';
import types from './types';
import Context, { initialState } from './appContext';
// components
import NotMobile from '../pages/NotMobile';
import { StatusError, ErrorComponent } from '../pages/Error';
import CameraError from '../components/Camera/CameraError';
import { CAMERA_ACCESS_ERROR } from '../constants/selfieTypes';
import ErrorSession from '../pages/Error/ErrorSession';
import errors from '../constants/errorTypes';

const fadingBackground = styled(BaseModalBackground)`
  opacity: ${(p) => p.opacity};
  transition: all 0.3s ease-in-out;
`;

const FullscreenDiv = styled.div`
  height: 100vh;
  width: 100vw;
  box-sizing: border-box;
`;

const muiTheme = createTheme();

function ContextProvider({ children }) {
  const [store, dispatch] = useReducer(reducer, initialState);
  const { error } = store;

  const setLoading = (payload) => {
    dispatch({
      type: types.SET_LOADING,
      payload,
    });
  };

  const setError = (payload) => {
    const fullPayload = { ...payload, error: true };
    dispatch({
      type: types.SET_ERROR,
      payload: fullPayload,
    });
  };

  const unsetError = () => {
    const err = {
      error: false,
      description: null,
      type: null,
    };
    dispatch({
      type: types.UNSET_ERROR,
      payload: err,
    });
  };

  const stepForward = () => {
    dispatch({
      type: types.STEP_FORWARD,
    });
  };

  const stepTwoForward = () => {
    dispatch({
      type: types.STEP_TWO_FORWARD,
    });
  };

  const stepBackwards = () => {
    dispatch({
      type: types.STEP_BACKWARDS,
    });
  };

  const setStep = (payload) => {
    dispatch({
      type: types.SET_STEP,
      payload,
    });
  };

  const setOperationData = (unparsed, parsed) => {
    dispatch({
      type: types.SET_OPERATION_DATA,
      payload: { unparsed, parsed },
    });
  };

  const setUserData = (payload) => {
    dispatch({
      type: types.SET_USER_DATA,
      payload,
    });
  };

  const setConfirmationData = (payload) => {
    dispatch({
      type: types.SET_CONFIRMATION_DATA,
      payload,
    });
  };

  const setContracts = (payload) => {
    dispatch({
      type: types.SET_CONTRACTS,
      payload,
    });
  };

  const setModal = (payload) => {
    dispatch({
      type: types.SET_MODAL,
      payload,
    });
  };

  const setPhoto = (payload) => {
    dispatch({
      type: types.SET_PHOTO,
      payload,
    });
  };

  const setCamera = (payload) => {
    dispatch({
      type: types.SET_CAMERA,
      payload,
    });
  };

  const dispatchActions = {
    setLoading,
    setError,
    unsetError,
    stepForward,
    stepTwoForward,
    stepBackwards,
    setStep,
    setOperationData,
    setUserData,
    setConfirmationData,
    setContracts,
    setModal,
    setPhoto,
    setCamera,
  };

  let selectError = null;
  if (error.response) {
    selectError = error.response.data.error
  };
  if (error.type === CAMERA_ACCESS_ERROR) {
    selectError = error.type
  };
  return (
    <MuiThemeProvider theme={muiTheme}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <GlobalStyles />
          <>
            {isMobile ? (
              <Context.Provider
                value={{
                  ...store,
                  ...dispatchActions,
                }}
              >
                <ModalProvider
                  theme={theme}
                  backgroundComponent={fadingBackground}
                >
                  <FullscreenDiv>
                    {error.error ?
                      <>
                        {selectError === errors.unauthorized && <ErrorSession />}
                        {selectError === errors.success && <StatusError status='success' />}
                        {selectError === errors.cancel && <StatusError status='cancel' />}
                        {selectError === CAMERA_ACCESS_ERROR && <CameraError />}
                        {(selectError !== errors.success
                          && selectError !== errors.cancel
                          && selectError !== errors.unauthorized
                          && selectError !== CAMERA_ACCESS_ERROR) ?
                          <ErrorComponent /> : null}
                      </>
                      : children}
                  </FullscreenDiv>
                </ModalProvider>
              </Context.Provider>
            ) : (
              <NotMobile />
            )}
          </>
        </ThemeProvider>
      </StyledEngineProvider>
    </MuiThemeProvider>
  );
}

export default ContextProvider;
